import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useRenameSession, useSessionName } from "@/hooks/use-sessions";
import { type Session } from "./session-model";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC } from "react";

type RenameSessionModalProps = {
    session: Session;
    open: boolean;
    onClose: () => void;
};

export const RenameSessionModal: FC<RenameSessionModalProps> = ({
    session,
    open,
    onClose,
}: RenameSessionModalProps) => {
    const { toast } = useToast();

    const name = useSessionName(session);
    const [newSessionName, setNewSessionName] = useState(name);
    useEffect(() => {
        setNewSessionName(name);
    }, [name]);

    const renameSession = useRenameSession(session);

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            try {
                await renameSession.mutateAsync(newSessionName);
                onClose();
            } catch (error) {
                toast({
                    title: "Failed to rename session",
                    description: formatError(error),
                });
            }
        },
        [newSessionName, onClose, renameSession, toast],
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-xl"
                data-track-location={TrackLocations.RenameSessionModal}
                data-testid="rename-session-modal"
            >
                <DialogHeader>
                    <DialogTitle>Rename session</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <DialogBody className="flex flex-col gap-4">
                        <InputField
                            label="Session name"
                            id="session-name"
                            hint="Clear the value to use the auto generated session name."
                        >
                            <Input
                                id="session-name"
                                autoFocus={true}
                                value={newSessionName}
                                onChange={(e) => setNewSessionName(e.target.value)}
                            />
                        </InputField>
                    </DialogBody>

                    <DialogFooter className="sm:justify-end">
                        <DialogClose asChild>
                            <Button type="button" variant="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button type="submit" data-testid="rename-session-dialog-save-button">
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
